const DEFAULT_ALLOWED_TAGS = new Set([
  "A",
  "BR",
  "EM",
  "STRONG",
  "SUB",
  "SUP",
  "U",
]);

const SAFE_URL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const BLOCKED_TAGS = new Set([
  "IFRAME",
  "OBJECT",
  "SCRIPT",
  "STYLE",
  "TEMPLATE",
]);

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function unwrapElement(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
}

function isSafeHref(value) {
  const href = String(value ?? "").trim();
  if (!href) return false;
  if (href.startsWith("#") || href.startsWith("/") || href.startsWith("./") || href.startsWith("../")) {
    return true;
  }
  try {
    return SAFE_URL_PROTOCOLS.has(new URL(href, window.location.origin).protocol);
  } catch(err) {
    console.log("Error checking safe href", err);
    return false;
  }
}

function getAllowedAttributeRule(allowedAttributes, tagName, attributeName) {
  if (!allowedAttributes || typeof allowedAttributes !== "object") return null;
  const tagRules = allowedAttributes[tagName] || allowedAttributes["*"];
  if (!tagRules || typeof tagRules !== "object") return null;
  return tagRules[attributeName] ?? null;
}

function stripUnsafeAttributes(el, allowedAttributes) {
  const attrs = Array.from(el.attributes);
  for (const attr of attrs) {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on") || name === "style" || name === "srcdoc") {
      el.removeAttribute(attr.name);
      continue;
    }

    if (el.tagName === "A" && name === "href") {
      if (isSafeHref(attr.value)) {
        el.setAttribute("rel", "noopener noreferrer");
        continue;
      }
    }

    const attributeRule = getAllowedAttributeRule(
      allowedAttributes,
      el.tagName,
      name
    );
    if (attributeRule === true) continue;
    if (typeof attributeRule === "function") {
      const normalized = attributeRule(attr.value, el);
      if (normalized === true) continue;
      if (typeof normalized === "string" && normalized) {
        el.setAttribute(attr.name, normalized);
        continue;
      }
    }

    el.removeAttribute(attr.name);
  }
}

export function sanitizeHtml(dirtyHtml, options = {}) {
  const allowedTags = options.allowedTags || DEFAULT_ALLOWED_TAGS;
  const container = document.createElement("div");
  container.innerHTML = String(dirtyHtml ?? "");

  const walk = (root) => {
    const childNodes = Array.from(root.childNodes);
    for (const child of childNodes) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        continue;
      }
      if (child.nodeType === Node.TEXT_NODE) continue;
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        continue;
      }

      walk(child);

      if (BLOCKED_TAGS.has(child.tagName)) {
        child.remove();
        continue;
      }

      if (!allowedTags.has(child.tagName)) {
        unwrapElement(child);
        continue;
      }

      stripUnsafeAttributes(child, options.allowedAttributes);
    }
  };

  walk(container);
  return container.innerHTML;
}

export function safeHtml(value) {
  return { __html: sanitizeHtml(value) };
}
